import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface GalleryDesignDTO {
  // The preview's cover paints the gallery's own title and shoot date
  // (`YYYY-MM-DD`, already text on the wire).
  title: string;
  shootDate: string | null;
  coverStyle: string;
  coverVideoUrl: string | null;
  coverVideoPosterUrl: string | null;
  theme: string;
  thumbnailSize: string;
  gridSpacing: string;
  barStyle: string;
  coverFont: string;
  // «<x>% <y>%», 0–100 each — already a CSS object-position value.
  coverFocus: string;
  // The gallery's manual cover, for the focus-point pad. Null = neutral pad.
  coverImageUrl: string | null;
  guestToken: string | null;
}

// The chip-set fields (closed value sets) — bulk edit enumerates exactly these.
// The video-cover pair writes through setCoverVideo (its own atomic endpoint
// with storage-ledger record/release), not the generic field-setter.
export type GalleryDesignChoiceField = 'coverStyle' | 'theme' | 'thumbnailSize' | 'gridSpacing' | 'barStyle' | 'coverFont';
// `coverFocus` is a free pair, not a chip set — it stays OUT of the choice
// union so the bulk-edit / preset maps over the closed sets keep compiling.
export type GalleryDesignField = GalleryDesignChoiceField | 'coverFocus';

export interface GalleryCoverVideoInput {
  url: string;
  fileId?: string;
  posterUrl?: string;
  posterFileId?: string;
}

export interface GalleryContactDTO {
  contactCardEnabled: boolean;
  contactName: string | null;
  contactWebsite: string | null;
  contactAvatarUrl: string | null;
  contactTelegram: string | null;
  contactWhatsapp: string | null;
  contactVk: string | null;
  contactPinterest: string | null;
  contactYoutube: string | null;
}

@Injectable({ providedIn: 'root' })
export class GalleryDesignApiService {
  private readonly http = inject(HttpClient);

  getDesign(galleryId: string): Promise<GalleryDesignDTO> {
    return firstValueFrom(this.http.get<GalleryDesignDTO>(`/api/gallery/${galleryId}/design`));
  }

  setField(galleryId: string, field: GalleryDesignField, value: string): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/design`, { field, value }));
  }

  // null clears the pair (and releases the uploads' storage claims server-side).
  setCoverVideo(galleryId: string, cover: GalleryCoverVideoInput | null): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/design/cover-video`, { cover }));
  }

  getContact(galleryId: string): Promise<GalleryContactDTO> {
    return firstValueFrom(this.http.get<GalleryContactDTO>(`/api/gallery/${galleryId}/contact`));
  }

  setContact(galleryId: string, contact: GalleryContactDTO): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/contact`, contact));
  }
}
